import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  CardMedia,
  Input,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, ChangeEvent, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useFormik } from "formik";
import * as Yup from "yup";

import useAuth from "../../hooks/useAuth";
import { useAppDispatch } from "../../hooks/store";
import { usePutUpdateProfileMutation } from "../../services/auth";
import { UploadMedia } from "../../utils/uploadMedia";
import { setCredentials } from "../../reducers/authSlice";
import { getFromStorage, showToast, showError } from "../../helpers";
import { STORAGE_KEYS } from "../../constants/storagekeys";

const EditProfile = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAuth();

  const [updateProfile] = usePutUpdateProfileMutation();

  const [image, setImage] = useState<string>("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [countryCode, setCountryCode] = useState(user?.countryCode || "+971");
  const [isLoading, setIsLoading] = useState(false);

  // Prefill user data
  useEffect(() => {
    if (user) {
      setImage(user?.image || "");
      setCountryCode(user?.dialCode || "+971");
      setPhoneDisplay((user?.dialCode || "+971") + (user?.phoneNo|| ""));
    }
  }, [user]);

  // Validation Schema
  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required("Name is required")
      .min(3, "Minimum 3 characters")
      .max(30, "Maximum 30 characters"),
    email: Yup.string()
      .required("Email is required")
      .email("Enter a valid email"),
    phoneNo: Yup.string()
      .required("Phone is required")
      .min(9, "At least 9 digits")
      .max(16, "At most 16 digits"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: user?.fullName || "",
      email: user?.email || "",
      phoneNo: user?.phoneNo || "",
      dialCode: user?.dialCode || countryCode,
      profile_picture: user?.image || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        setIsLoading(true);

        const body = {
          fullName: values.firstName,
          email: values.email,
          phoneNo: values.phoneNo,
          dialCode: values.dialCode,
          image: image,
        };

        const response = await updateProfile(body as any).unwrap();
        if (response?.statusCode === 200) {
          const token = getFromStorage(STORAGE_KEYS.token);
          showToast("Profile updated successfully");
          dispatch(
            setCredentials({
              user: response?.data,
              token,
            })
          );
          navigate("/dashboard");
        }
      } catch (err) {
        showError("Error updating profile");
      } finally {
        setIsLoading(false);
        setSubmitting(false);
      }
    },
  });

  // Handle image upload
  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    try {
      if (!file) {
        setImage("");
        formik.setFieldValue("profile_picture", "");
        return;
      }
      const res = await UploadMedia(file);
      const imageUrl = res?.data?.images?.[0]?.url || res?.data?.image || "";
      if (imageUrl) {
        setImage(imageUrl);
        formik.setFieldValue("profile_picture", imageUrl);
        showToast("Image uploaded successfully");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      showError("Image upload failed");
    }
  };

  // Handle phone input
  const handleChangePhone = (phone: string, country: any) => {
    formik.setFieldValue("phoneNo", phone.replace(country.dialCode, ""));
    formik.setFieldValue("dialCode", country.dialCode);
    setCountryCode(country.dialCode);
    setPhoneDisplay(phone);
  };

  return (
    <>
      <div className="main_title">
        <h1>Edit Profile</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {" "} - <button onClick={() => navigate("/manage-profile")}>Manage Profile</button>
          {" "} - Edit Profile
        </p>
      </div>

      <div className="cards">
        <form onSubmit={formik.handleSubmit} className="form">
          <div className="gap_p">
            {/* Image Upload */}
            <div className="control_group w_100">
              <label>Upload Image</label>
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="photo" />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        formik.setFieldValue("profile_picture", "");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={handleImageUpload}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img
                          src="/static/images/edit2_icon.svg"
                          alt="icon"
                        />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            {/* Name */}
            <div className="control_group w_33">
              <label>Name</label>
              <TextField
                name="firstName"
                placeholder="Enter Name"
                hiddenLabel
                fullWidth
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </div>

            {/* Email */}
            <div className="control_group w_33">
              <label>Email Id</label>
              <TextField
                name="email"
                type="email"
                placeholder="Enter Email"
                hiddenLabel
                fullWidth
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled
              />
            </div>

            {/* Phone */}
            <div className="control_group w_33">
              <label>Phone Number</label>
              <PhoneInput
                country={"ae"}
                placeholder="Enter Phone Number"
                enableSearch={false}
                autoFormat={false}
                inputStyle={{ width: "100%" }}
                value={phoneDisplay}
                onChange={handleChangePhone}
                onBlur={formik.handleBlur}
              />
              {formik.touched.phoneNo && formik.errors.phoneNo && (
                <div className="err_msg">{formik.errors.phoneNo}</div>
              )}
            </div>
          </div>

          <div className="form_btn">
            <Button
              className="btn btn_primary"
              type="submit"
              disabled={isLoading || formik.isSubmitting}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditProfile;
